import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Linkedin, Youtube, Instagram, Facebook, MessageCircle, ArrowRight, Lock } from "lucide-react";
import { MiaLogoMark } from "./Navbar";

export default function Footer() {
  return (
    <footer className="bg-[color:var(--mia-navy)] text-white" data-testid="site-footer">
      <div className="mia-container py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
        {/* Brand */}
        <div>
          <Link to="/" className="flex items-center gap-3">
            <MiaLogoMark className="w-10 h-10" />
            <div className="leading-tight">
              <div className="font-display font-black text-white text-sm">MARINE INFRASTRUCTURE</div>
              <div className="font-display font-black text-white/70 text-sm">AFRICA</div>
            </div>
          </Link>
          <p className="mt-4 text-white/70 text-sm leading-relaxed">
            Delivering innovative and sustainable marine and waterfront infrastructure solutions across Africa.
          </p>
          <div className="mt-5 flex gap-2 flex-wrap">
            {[Linkedin, Youtube, Instagram, Facebook].map((Icon, i) => (
              <a key={i} href="#" aria-label="social" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-[color:var(--mia-green)] hover:border-[color:var(--mia-green)] transition-colors" data-testid={`social-${i}`}>
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <div className="font-display font-bold text-white text-sm tracking-[0.14em] uppercase">Quick Links</div>
          <div className="w-8 h-[3px] bg-[color:var(--mia-green)] mt-3 mb-4" />
          <ul className="space-y-2.5 text-sm text-white/75">
            <li><Link to="/" className="hover:text-[color:var(--mia-green)]">Home</Link></li>
            <li><Link to="/about" className="hover:text-[color:var(--mia-green)]">About Us</Link></li>
            <li><Link to="/solutions" className="hover:text-[color:var(--mia-green)]">Our Solutions</Link></li>
            <li><Link to="/contact" className="hover:text-[color:var(--mia-green)]">Contact Us</Link></li>
            <li><Link to="/careers" className="hover:text-[color:var(--mia-green)]">Join Our Team</Link></li>
            <li><Link to="/request-consultation" className="hover:text-[color:var(--mia-green)]">Request Consultation</Link></li>
          </ul>
        </div>

        {/* Our Solutions */}
        <div>
          <div className="font-display font-bold text-white text-sm tracking-[0.14em] uppercase">Our Solutions</div>
          <div className="w-8 h-[3px] bg-[color:var(--mia-green)] mt-3 mb-4" />
          <ul className="space-y-2.5 text-sm text-white/75">
            <li><Link to="/solutions/floating-infrastructure-solutions" className="hover:text-[color:var(--mia-green)]">Floating Infrastructure</Link></li>
            <li><Link to="/solutions/marine-navigation-safety-solutions" className="hover:text-[color:var(--mia-green)]">Marine Navigation & Safety</Link></li>
            <li><Link to="/solutions/marine-access-berthing-solutions" className="hover:text-[color:var(--mia-green)]">Marine Access & Berthing</Link></li>
            <li><Link to="/solutions/coastal-waterfront-solutions" className="hover:text-[color:var(--mia-green)]">Coastal & Waterfront</Link></li>
            <li><Link to="/solutions/marine-coating-solutions" className="hover:text-[color:var(--mia-green)]">Coating Solutions</Link></li>
            <li><Link to="/solutions/marine-vessels-equipment-solutions" className="hover:text-[color:var(--mia-green)]">Vessels & Equipment</Link></li>
          </ul>
        </div>

        {/* Contact & CTAs */}
        <div>
          <div className="font-display font-bold text-white text-sm tracking-[0.14em] uppercase">Contact Us</div>
          <div className="w-8 h-[3px] bg-[color:var(--mia-green)] mt-3 mb-4" />
          <ul className="space-y-3 text-sm text-white/75">
            <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-[color:var(--mia-green)] shrink-0" /> <span className="break-all">+234 000 000 0000</span></li>
            <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-[color:var(--mia-green)] shrink-0" /> <span className="break-all text-xs sm:text-sm">info@marineinfrastructure.africa</span></li>
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[color:var(--mia-green)] shrink-0" /> Lagos, Nigeria</li>
          </ul>
          <div className="mt-5 flex flex-col gap-2">
            <a href="https://wa.me/2340000000000" target="_blank" rel="noopener noreferrer" className="mia-btn mia-btn-primary w-full text-xs" data-testid="footer-whatsapp">
              <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
            </a>
            <Link to="/request-consultation" className="mia-btn mia-btn-outline-white w-full text-xs" data-testid="footer-consultation">
              Request Consultation <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mia-container py-5 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/55 text-center md:text-left">
          <div data-testid="footer-copyright">© {new Date().getFullYear()} Marine Infrastructure Africa Ltd. All Rights Reserved.</div>
          <div className="flex gap-5 flex-wrap justify-center items-center">
            <a href="#" className="hover:text-[color:var(--mia-green)]">Privacy Policy</a>
            <a href="#" className="hover:text-[color:var(--mia-green)]">Terms of Use</a>
            <Link to="/admin/login" className="inline-flex items-center gap-1.5 text-white/40 hover:text-[color:var(--mia-green)] transition-colors" data-testid="footer-system-access">
              <Lock className="w-3 h-3" /> System Access
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
